import { Region } from 'contentstack';
import React, { ReactNode, useEffect, useReducer } from 'react';
import ContentService from '../../services/content';
import { useConfig } from '../config-provider';
import { ContentContext, INITIAL_STATE } from './hook';
import { ContentReducer } from './reducer';
export default function ContentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(ContentReducer, INITIAL_STATE);
  const { config } = useConfig();
  useEffect(() => {
    const contentConfig = config?.services.contentstack;
    if (!contentConfig) return;
    const contentService = new ContentService({
      api_key: contentConfig.apiKey,
      delivery_token: contentConfig.deliveryToken,
      environment: contentConfig.environment,
      region: contentConfig.region as Region,
      branch: contentConfig.branch,
      fetchOptions: contentConfig.fetchOptions ?? {
        timeout: 8000,
        retryLimit: 2,
        retryDelayOptions: { base: 500 },
        keepalive: true,
        retryCondition: (error: any) => {
          // Retry on network/abort/timeouts; avoid retrying on 4xx
          const status = (error && (error.status || error.code)) as number | string | undefined;
          const isNetworkish = !status || status === 0 || String(status).includes('ECONN') || String(status).includes('ETIMEDOUT');
          const isAbort = error?.name === 'AbortError';
          const is5xx = typeof status === 'number' && status >= 500;
          const is4xx = typeof status === 'number' && status >= 400 && status < 500;
          return isNetworkish || isAbort || is5xx ? true : !is4xx;
        },
        logHandler: (level: string, data: any) => {
          if (level === 'error') {
            // Lightweight client-side logging; adjust if you have a logger
            console.warn('Contentstack fetch error:', data);
          }
        },
      },
      live_preview: {
        management_token: contentConfig.livePreview?.management_token ??  '',
        enable: contentConfig.livePreview?.enabled,
        host: contentConfig.livePreview?.host ?? 'cdn.contentstack.com',
      },
    });
    dispatch({ type: 'CONFIG', payload: contentService });
  }, [config, dispatch]);
  return (
    <ContentContext.Provider value={{ ...state, dispatch }}>{children}</ContentContext.Provider>
  );
}
